/**
 * Created by andrewhurst on 10/5/15.
 */
import React, { useState, useEffect } from 'react';
import { Keyboard, LayoutAnimation, View, Dimensions, Platform, StyleSheet } from 'react-native';
import type { KeyboardEvent, LayoutAnimationConfig, LayoutAnimationType, ViewStyle } from 'react-native';

const styles = StyleSheet.create({
	container: {
		left: 0,
		right: 0,
		bottom: 0,
	},
});

// From: https://medium.com/man-moon/writing-modern-react-native-ui-e317ff956f02
const defaultAnimation = {
	duration: 500,
	create: {
		duration: 300,
		type: LayoutAnimation.Types.easeInEaseOut,
		property: LayoutAnimation.Properties.opacity,
	},
	update: {
		type: LayoutAnimation.Types.spring,
		springDamping: 200,
	},
};

interface KeyboardSpacerProps {
	topSpacing?: number;
	onToggle?: (isOpen: boolean, space: number) => void;
	style?: ViewStyle;
}

export default function KeyboardSpacer({ topSpacing = 0, onToggle = () => null, style }: KeyboardSpacerProps) {
	const [keyboardSpace, setKeyboardSpace] = useState(0);

	useEffect(() => {
		const updateListener = Platform.OS === 'android' ? 'keyboardDidShow' : 'keyboardWillShow';
		const resetListener = Platform.OS === 'android' ? 'keyboardDidHide' : 'keyboardWillHide';

		const listeners = [
			Keyboard.addListener(updateListener, updateKeyboardSpace),
			Keyboard.addListener(resetListener, resetKeyboardSpace),
		];

		return () => {
			listeners.forEach((listener) => listener.remove());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [topSpacing]);

	function updateKeyboardSpace(event: KeyboardEvent) {
		if (!event.endCoordinates) {
			return;
		}

		let animationConfig: LayoutAnimationConfig = defaultAnimation;
		if (Platform.OS === 'ios' && event.easing) {
			animationConfig = LayoutAnimation.create(
				event.duration,
				LayoutAnimation.Types[event.easing as LayoutAnimationType],
				LayoutAnimation.Properties.opacity,
			);
		}
		LayoutAnimation.configureNext(animationConfig);

		// get updated on rotation
		const screenHeight = Dimensions.get('window').height;
		// when external physical keyboard is connected event.endCoordinates.height still equals virtual keyboard
		// height however only the keyboard toolbar is showing if there should be one
		const space = screenHeight - event.endCoordinates.screenY + topSpacing;
		setKeyboardSpace(space);
		onToggle(true, space);
	}

	function resetKeyboardSpace(event: KeyboardEvent) {
		let animationConfig: LayoutAnimationConfig = defaultAnimation;
		if (Platform.OS === 'ios' && event.easing) {
			animationConfig = LayoutAnimation.create(
				event.duration,
				LayoutAnimation.Types[event.easing as LayoutAnimationType],
				LayoutAnimation.Properties.opacity,
			);
		}
		LayoutAnimation.configureNext(animationConfig);

		setKeyboardSpace(0);
		onToggle(false, 0);
	}

	return <View style={[styles.container, { height: keyboardSpace }, style]} />;
}
